import { useMediaQuery } from '@react-hookz/web';
import { useCallback, useState } from 'react';

import { useAccountContext } from '~context/account';
import { useAuthActionsContext } from '~context/auth';
import { usePaywallContext } from '~context/paywall';
import {
	PaymentReason,
	PaymentReasonType,
	RegisterInbound,
	RegisterOutbound,
} from '~features/annals';
import { annals } from '~features/annals/hooks';
import { useCurrentBot } from '~hooks/useCurrentBot';
import { useFreeGiftWatcher } from '~hooks/useFreeGiftAvailable';
import { useSetLastBotId } from '~hooks/useLastBotId';
import { useSession } from '~hooks/useSession';
import { useTranslations } from '~i18n/hooks';
import CloseIcon from '~public/assets/icons/close.svg';
import { dialogsApi } from '~services/api/dialogs';
import type { TGift } from '~services/api/gifts';
import { giftsApi } from '~services/api/gifts';
import { ButtonIconGhost } from '~shared/ui/molecules';
import { buildMessageFromString } from '~utils/messages';

import { Gift } from './Gift';
import { OpenFeed } from './OpenFeed';
import { OpenFeedMobile } from './OpenFeedMobile';
import {
	actionButtonWrapperPosition,
	CloseFeedMobileLayout,
	Container,
	GiftFeedGrid,
	OpenGiftFeedMobileLayout,
} from './styles';

const SIZE = 6;

type Props = {
	recipientId: string;
	userId: string;
};

export const GiftFeed = ({ recipientId, userId }: Props) => {
	const t = useTranslations('Chats');
	const isMobile = useMediaQuery('screen and (max-width: 767px)');
	const { isDemoUser } = useSession();
	const { openRegister } = useAuthActionsContext();
	const [feedOpened, setFeedOpened] = useState(false);

	const { balance } = useAccountContext();
	const { openPaywall } = usePaywallContext();

	const { bot } = useCurrentBot({ recipientId });
	const setLastBotId = useSetLastBotId();

	const giftFeedAnnals = annals.chat.useOpenGiftFeed({
		botName: bot.name,
		botClientId: bot.clientId,
	});

	const [sendMessage] = dialogsApi.useSendMessageMutation();

	const { data, isLoading, isError } = giftsApi.useGetGiftsQuery({
		userId,
		botId: recipientId,
	});
	const freeGift = (data || []).find((g) => g.price == 0) || { id: '' };

	useFreeGiftWatcher(recipientId, freeGift.id);

	const handleOpen = () => {
		setFeedOpened(true);
		void giftFeedAnnals();
	};
	const handleClose = () => {
		setFeedOpened(false);
	};

	const sendGift = useCallback(
		(gift: TGift) => {
			void sendMessage({
				recipientId,
				id: userId,
				message: buildMessageFromString('', userId, {
					meta: {
						gift: gift.id,
						giftPreview: gift.image,
						messageType: 'public-photo',
						publicMediaId: gift.mediaId,
					},
					reference: gift.image,
				}),
			});
			setLastBotId(recipientId);
		},
		[userId, recipientId, sendMessage, setLastBotId]
	);

	const handleSendGift = useCallback(
		(gift: TGift) => {
			if (isDemoUser) {
				openRegister({
					analytics: {
						inbound:
							bot.accountType === 'user-generated'
								? RegisterInbound.ChatSendGiftUDC
								: RegisterInbound.ChatSendGift,
						outbound: RegisterOutbound.Chat,
					},
				});
				return;
			}
			if (balance < gift.price) {
				openPaywall({
					prefer: 'neurons',
					cost: gift.price,
					botName: bot.name,
					reason: PaymentReason.Gift,
					reasonType: PaymentReasonType.Chat,
					botClientId: bot.clientId,
					texts: {
						neurons: t('chatGiftNeuronsPaywallText', { name: bot?.name || '' }),
					},
					callback: (success: boolean) => {
						if (success) {
							sendGift(gift);
						}
					},
				})();
				return;
			}

			sendGift(gift);
		},
		[
			balance,
			bot.clientId,
			bot.name,
			bot.accountType,
			isDemoUser,
			openPaywall,
			openRegister,
			sendGift,
			t,
		]
	);

	if ((!isLoading && isError) || !data?.length) {
		return null;
	}

	const gifts =
		data.length <= SIZE || feedOpened || isMobile ? data : data.slice(0, SIZE);

	return (
		<>
			<Container opened={feedOpened && (isMobile || data.length > SIZE)}>
				<GiftFeedGrid
					desktopCols={data.length > SIZE ? SIZE : data.length}
					opened={feedOpened && (isMobile || data.length > SIZE)}
				>
					{gifts.map((gift) => {
						return (
							<Gift
								key={gift.id}
								gift={gift}
								showPrice={!isDemoUser}
								onPress={handleSendGift}
							/>
						);
					})}
				</GiftFeedGrid>
				{!isMobile && data.length > SIZE && (
					<>
						{feedOpened ? (
							<ButtonIconGhost
								className={actionButtonWrapperPosition}
								leftIcon={<CloseIcon />}
								onClick={handleClose}
							/>
						) : (
							<OpenFeed onClick={handleOpen} />
						)}
					</>
				)}

				<CloseFeedMobileLayout>
					<ButtonIconGhost leftIcon={<CloseIcon />} onClick={handleClose} />
				</CloseFeedMobileLayout>
			</Container>

			<OpenGiftFeedMobileLayout hidden={feedOpened}>
				<OpenFeedMobile gift={gifts[0]} onClick={handleOpen} />
			</OpenGiftFeedMobileLayout>
		</>
	);
};
